/**
 * Modifier stack (FS-05 §3.4). Hulls, power-ups and assists all compose here in a fixed order:
 * base hull → upgrades → active power-up → assists. resolve() always starts from the Design Bible §7
 * base and reapplies every layer, so a power-up expiring exactly undoes itself — no "restore old
 * value" logic anywhere.
 */
import {
  BANK_MAX,
  DAMPING,
  GRAZE_RADIUS,
  LATERAL_ACCEL,
  MAX_LATERAL_SPEED,
  SHIP_HITBOX_RADIUS,
} from '../core/constants';
import { clamp } from '../core/math-det';

export interface ResolvedModifiers {
  lateralAccel: number;
  maxLateralSpeed: number;
  damping: number;
  hitboxRadius: number;
  grazeRadius: number;
  bankMax: number;
  worldSpeedScale: number;
  intangible: boolean;
  beatAwardBonus: number;
}

// Per-field bounds (FS-05 §5.5). No stack may produce a 0 hitbox or infinite speed.
const BOUNDS = {
  lateralAccel: [40, 130],
  maxLateralSpeed: [14, 40],
  damping: [3.0, 12.0],
  hitboxRadius: [0.2, 0.9],
  grazeRadius: [0.8, 2.4],
  bankMax: [0, (55 * Math.PI) / 180],
  worldSpeedScale: [0.35, 1.2],
  beatAwardBonus: [1.0, 3.0],
} as const;

export interface ModifierInput {
  /** From the active power-up (FS-07). */
  intangible: boolean;
  worldSpeedScale: number;
  /** Hull deltas (FS-10) — kept simple for the core slice. */
  hitboxScale: number;
  accelScale: number;
  beatAwardBonus: number;
  /** Assists (Design Bible §12). */
  assistHitbox: boolean; // ×1.5
  assistSpeed: boolean; // world ×0.75
}

export function baseModifiers(): ResolvedModifiers {
  return {
    lateralAccel: LATERAL_ACCEL,
    maxLateralSpeed: MAX_LATERAL_SPEED,
    damping: DAMPING,
    hitboxRadius: SHIP_HITBOX_RADIUS,
    grazeRadius: GRAZE_RADIUS,
    bankMax: BANK_MAX,
    worldSpeedScale: 1.0,
    intangible: false,
    beatAwardBonus: 1.0,
  };
}

function clampField(m: ResolvedModifiers): void {
  m.lateralAccel = clamp(m.lateralAccel, BOUNDS.lateralAccel[0], BOUNDS.lateralAccel[1]);
  m.maxLateralSpeed = clamp(m.maxLateralSpeed, BOUNDS.maxLateralSpeed[0], BOUNDS.maxLateralSpeed[1]);
  m.damping = clamp(m.damping, BOUNDS.damping[0], BOUNDS.damping[1]);
  m.hitboxRadius = clamp(m.hitboxRadius, BOUNDS.hitboxRadius[0], BOUNDS.hitboxRadius[1]);
  m.grazeRadius = clamp(m.grazeRadius, BOUNDS.grazeRadius[0], BOUNDS.grazeRadius[1]);
  m.bankMax = clamp(m.bankMax, BOUNDS.bankMax[0], BOUNDS.bankMax[1]);
  m.worldSpeedScale = clamp(m.worldSpeedScale, BOUNDS.worldSpeedScale[0], BOUNDS.worldSpeedScale[1]);
  m.beatAwardBonus = clamp(m.beatAwardBonus, BOUNDS.beatAwardBonus[0], BOUNDS.beatAwardBonus[1]);
}

/** Recompute from base and reapply every layer into `out`. Allocation-free. */
export function resolveModifiers(inp: ModifierInput, out: ResolvedModifiers): void {
  const base = baseModifiers();
  out.lateralAccel = base.lateralAccel * inp.accelScale;
  out.maxLateralSpeed = base.maxLateralSpeed;
  out.damping = base.damping;
  out.hitboxRadius = base.hitboxRadius * inp.hitboxScale;
  out.grazeRadius = base.grazeRadius;
  out.bankMax = base.bankMax;
  out.worldSpeedScale = base.worldSpeedScale * inp.worldSpeedScale;
  out.intangible = inp.intangible;
  out.beatAwardBonus = inp.beatAwardBonus;

  // Layer 3 — assists.
  if (inp.assistHitbox) out.hitboxRadius *= 1.5;
  if (inp.assistSpeed) out.worldSpeedScale *= 0.75;

  clampField(out);
}
